// src/components/InfoCenter.js
import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Home, X, Mail, ChevronDown, Menu } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import BlogPost from './BlogPost';
import { blogPostData, loanCompanyPosts } from './BlogPostData';
import { getArticleSeo, SITE_BASE_URL } from './blogUtils';
import ResourceSection from './ResourceSection';
import { ResourceData } from './ResourceData';
import PopularLinksTab from './PopularLinksTab'; // import your new component
import { useLocation } from 'react-router-dom';


// 1) Import your popups
import AuthPopup from './AuthPopup';
import PaymentPopup from './PaymentPopup';

const MEDIA_BASE_URL = 'https://media.satoshiadvance.com/images';


// Serve the lightweight stored thumbnail for self-hosted blog images;
// external media URLs pass through untouched
const withThumb = (url) =>
  url && url.includes('/api/blog-images/') ? `${url}?w=320` : url;

// Compact browsable listing row: small thumbnail left, title + one-line
// quote right, so several articles fit on one screen
function ArticleRow({ image, title, tagline, date, quote, slug, isLast, isDesktop, onOpen }) {
  return (
    <motion.div
      whileHover={{ backgroundColor: 'rgba(0, 102, 204, 0.05)' }}
      onClick={() => onOpen(slug)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: isDesktop ? '1rem' : '0.75rem',
        padding: isDesktop ? '0.9rem 0.5rem' : '0.75rem 0.25rem',
        cursor: 'pointer',
        borderBottom: isLast ? 'none' : '1px solid #ececec'
      }}
    >
      <img
        src={withThumb(image)}
        alt={title}
        referrerPolicy="no-referrer"
        loading="lazy"
        style={{
          width: isDesktop ? '120px' : '92px',
          height: isDesktop ? '84px' : '66px',
          objectFit: 'cover',
          borderRadius: '8px',
          flexShrink: 0,
          backgroundColor: '#f0f0f0'
        }}
      />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '0.75rem', color: '#0066cc', fontWeight: 600, marginBottom: '0.2rem' }}>
          {tagline}
          {tagline && date ? ' \u2022 ' : ''}
          <span style={{ color: '#888', fontWeight: 400 }}>{date}</span>
        </div>
        <h3
          style={{
            fontSize: isDesktop ? '1.05rem' : '0.95rem',
            fontWeight: 700,
            color: '#1a1a1a',
            margin: '0 0 0.25rem',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {title}
        </h3>
        <p
          style={{
            fontSize: '0.85rem',
            color: '#666',
            margin: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {quote}
        </p>
      </div>
    </motion.div>
  );
}

function ArticleListCard({ items, isDesktop, onOpen }) {
  return (
    <div
      style={{
        width: '100%',
        maxWidth: isDesktop ? '840px' : '100%',
        margin: isDesktop ? '0 auto 2rem' : '0 0 1.5rem 0',
        backgroundColor: '#fff',
        borderRadius: isDesktop ? '15px' : '10px',
        boxShadow: isDesktop ? '0 4px 24px rgba(0, 0, 0, 0.1)' : '0 1px 6px rgba(0, 0, 0, 0.08)',
        padding: isDesktop ? '0.5rem 1rem' : '0.25rem 0.75rem',
        boxSizing: 'border-box'
      }}
    >
      {items.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#666', fontSize: '0.9rem', padding: '1.25rem 0' }}>
          No matching articles.
        </p>
      ) : (
        items.map((item, i) => (
          <ArticleRow
            key={item.slug || i}
            {...item}
            isLast={i === items.length - 1}
            isDesktop={isDesktop}
            onOpen={onOpen}
          />
        ))
      )}
    </div>
  );
}

function InfoCenter() {
  const { articleSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Desktop vs. mobile
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  // 2) Popup states
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [amount, setAmount] = useState(50); // Default to $50 (annual plan)

  // Check if article is in blogPostData or loanCompanyPosts
  const isInBlogPosts = articleSlug && blogPostData[articleSlug];
  const isInLoanPosts = articleSlug && loanCompanyPosts && loanCompanyPosts[articleSlug];

  // Currently selected article, or null
  const currentArticle = isInBlogPosts
    ? blogPostData[articleSlug]
    : isInLoanPosts
      ? loanCompanyPosts[articleSlug]
      : null;

  // Default tab state
  const [selectedOption, setSelectedOption] = useState(() => {
    if (isInLoanPosts) return 'Top Loans';
    if (isInBlogPosts) return 'News & Blogs';
    return 'News & Blogs';
  });

  // Current date for "Read Next" etc.
  const currentDate = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  useEffect(() => {
    if (location.state && location.state.selectTab) {
      setSelectedOption(location.state.selectTab);
    }
  }, [location]);


  // Handle articleSlug changes
  useEffect(() => {
    if (articleSlug) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      if (isInLoanPosts) {
        setSelectedOption('Top Loans');
      } else if (isInBlogPosts) {
        setSelectedOption('News & Blogs');
      }
    }
  }, [articleSlug, isInLoanPosts, isInBlogPosts]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listing search + category filter (reset when the tab changes)
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  useEffect(() => {
    setSearchQuery('');
    setCategoryFilter('All');
  }, [selectedOption]);

  const browseFilter = (items) => {
    const q = searchQuery.trim().toLowerCase();
    return items.filter((it) =>
      (categoryFilter === 'All' || it.tagline === categoryFilter) &&
      (!q || `${it.title} ${it.quote || ''} ${it.tagline || ''}`.toLowerCase().includes(q))
    );
  };

  // Search box + horizontally scrollable category chips (plain JSX, not a
  // nested component, so the input keeps focus across re-renders)
  const browseControls = (items) => {
    const categories = ['All', ...new Set(items.map((it) => it.tagline).filter(Boolean))];
    return (
      <div
        style={{
          width: '100%',
          maxWidth: isDesktop ? '840px' : '100%',
          margin: isDesktop ? '0 auto 0.75rem' : '0 0 0.6rem 0',
          boxSizing: 'border-box'
        }}
      >
        <input
          type="search"
          placeholder="Search articles..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '0.6rem 0.9rem',
            fontSize: '16px',
            border: '1px solid #ddd',
            borderRadius: '10px',
            outline: 'none',
            backgroundColor: '#fff',
            marginBottom: '0.6rem'
          }}
        />
        {categories.length > 2 && (
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              paddingBottom: '0.25rem'
            }}
          >
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setCategoryFilter(cat)}
                style={{
                  flexShrink: 0,
                  padding: '0.35rem 0.8rem',
                  borderRadius: '999px',
                  border: categoryFilter === cat ? '1px solid #0066cc' : '1px solid #ddd',
                  backgroundColor: categoryFilter === cat ? '#0066cc' : '#fff',
                  color: categoryFilter === cat ? '#fff' : '#444',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Floating back-to-top button once the reader has scrolled a ways
  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Convert blog data to arrays for rendering
  const newsAndBlogs = Object.values(blogPostData).map(post => ({
    id: post.id,
    title: post.title,
    date: post.date,
    tagline: post.category,
    quote: post.mainQuote,
    slug: post.slug,
    image: post.headerImage,
    isBlogPost: true
  }));

  const loanCompanyCards = loanCompanyPosts
    ? Object.values(loanCompanyPosts).map(post => ({
      id: post.id,
      title: post.title,
      date: post.date,
      tagline: post.category,
      quote: post.mainQuote,
      slug: post.slug,
      image: post.headerImage,
      isBlogPost: true,
      isLoanPost: true
    }))
    : [];

  const sections = {

    'Top Loans': loanCompanyCards.length > 0
      ? loanCompanyCards
      : [
        {
          image: `${MEDIA_BASE_URL}/RetroLoans1.png`,
          title: 'Best Lending Platforms',
          quote: 'Compare and choose the right platform for your needs.',
          content: 'Discover top-rated cryptocurrency lending platforms...'
        },
        {
          image: `${MEDIA_BASE_URL}/RetroLoans2.png`,
          title: 'Featured Loan Opportunities',
          quote: 'Maximize your crypto lending potential.',
          content: 'Explore current lending opportunities across various platforms.'
        }
      ],
    'News & Blogs': newsAndBlogs,
    'Popular Links': [
      {
        image: `${MEDIA_BASE_URL}/RetroLinks1.png`,
        title: 'Essential Resources',
        quote: 'Your gateway to crypto lending excellence.',
        content: 'Access our carefully selected collection of links...'
      },
      {
        image: `${MEDIA_BASE_URL}/RetroLinks2.png`,
        title: 'Community Favorites',
        quote: 'Trusted by the crypto community.',
        content: 'Discover community-recommended platforms and tools.'
      }
    ],

    'Resources': ResourceData
  };

  // Filter the content based on the articleSlug
  const filteredContent = () => {
    if (!articleSlug) {
      if (selectedOption === 'Top Loans') {
        return [];
      }
      return sections[selectedOption];
    }
    if (selectedOption === 'News & Blogs' && isInBlogPosts) {
      return sections['News & Blogs'].filter(item => item.slug !== articleSlug);
    } else if (selectedOption === 'Top Loans' && isInLoanPosts) {
      return sections['Top Loans'].filter(item => item.slug !== articleSlug);
    } else {
      return sections[selectedOption];
    }
  };

  const getOrderedLoanPosts = () => {
    if (!loanCompanyPosts) return [];
    const orderedSlugs = ['gemini-exchange', 'milo-crypto-lending'];
    let allPosts = Object.values(loanCompanyPosts);

    let result = [];
    orderedSlugs.forEach(slug => {
      const post = allPosts.find(p => p.slug === slug);
      if (post) {
        result.push(post);
      }
    });

    const remainingPosts = allPosts.filter(post => !orderedSlugs.includes(post.slug));
    result = [...result, ...remainingPosts];
    return result;
  };

  const getRelatedArticles = () => {
    if (!currentArticle) return [];
    const currentSection = isInBlogPosts ? 'News & Blogs' : 'Top Loans';
    const sectionArticles = currentSection === 'News & Blogs'
      ? Object.values(blogPostData)
      : Object.values(loanCompanyPosts || {});

    return sectionArticles.filter(post => post.slug !== articleSlug);
  };

  const handleArticleClick = slug => {
    navigate(`/info-center/${slug}`);
  };

  // Previous / next article links at the bottom of an open article
  function PrevNextNav() {
    const ordered = isInBlogPosts ? Object.values(blogPostData) : getOrderedLoanPosts();
    const idx = ordered.findIndex((p) => p.slug === articleSlug);
    if (idx === -1) return null;
    const prev = idx > 0 ? ordered[idx - 1] : null;
    const next = idx < ordered.length - 1 ? ordered[idx + 1] : null;
    if (!prev && !next) return null;
    const cell = (label, post, align) => (
      post ? (
        <div
          key={label}
          onClick={() => handleArticleClick(post.slug)}
          style={{
            flex: 1,
            minWidth: 0,
            cursor: 'pointer',
            backgroundColor: '#fff',
            border: '1px solid #e5e5e5',
            borderRadius: '10px',
            padding: '0.75rem 0.9rem',
            textAlign: align,
            boxShadow: '0 1px 6px rgba(0, 0, 0, 0.06)'
          }}
        >
          <div style={{ fontSize: '0.75rem', color: '#0066cc', fontWeight: 600, marginBottom: '0.25rem' }}>
            {label}
          </div>
          <div
            style={{
              fontSize: '0.9rem',
              fontWeight: 700,
              color: '#1a1a1a',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {post.title}
          </div>
        </div>
      ) : (
        <div key={label} style={{ flex: 1 }} />
      )
    );
    return (
      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          width: '100%',
          maxWidth: isDesktop ? '760px' : '100%',
          margin: '1.5rem auto 0',
          boxSizing: 'border-box'
        }}
      >
        {cell('\u2190 Previous', prev, 'left')}
        {cell('Next \u2192', next, 'right')}
      </div>
    );
  }

  const handleHomeClick = () => {
    window.location.href = '/';
  };

  // Updated HeaderBar component with centered title
  function HeaderBar() {
    const [menuOpen, setMenuOpen] = useState(false);

    // Create refs for the menu and button
    const menuRef = useRef(null);
    const buttonRef = useRef(null);

    // Add click outside handler
    useEffect(() => {
      function handleClickOutside(event) {
        // Close menu if click is outside both the menu and the button
        if (
          menuOpen &&
          menuRef.current &&
          !menuRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target)
        ) {
          setMenuOpen(false);
        }
      }

      // Add event listener
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);

      // Clean up
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }, [menuOpen]);

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          width: '100%',
          backgroundColor: '#fff',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Header Bar */}
        <div
          style={{
            width: '100%',
            maxWidth: '1000px',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            padding: '0.75rem 0.75rem 0.75rem 1rem',
            boxSizing: 'border-box',
            position: 'relative', // Make position relative for absolute positioning of title
          }}
        >
          {/* Left side - Home icon only */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={handleHomeClick}
          >
            <Home size={20} color="#0066cc" />
          </motion.div>

          {/* Center - Title */}
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                fontWeight: '600',
                fontSize: '1.1rem',
                color: '#0066cc',
              }}
            >
              Satoshi Advance News
            </span>
          </div>

          {/* Right side - Icon and Dropdown */}
          <div style={{ marginLeft: 'auto' }}>
            <motion.div
              ref={buttonRef} // Add ref to the button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              style={{
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '50%',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
              }}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {/* Use lucide-react's Menu / X icons */}
              {menuOpen ? (
                <X size={20} strokeWidth={2} color="#0066cc" />
              ) : (
                <Menu size={20} strokeWidth={2} color="#0066cc" />
              )}
            </motion.div>

            {/* Dropdown Menu - Fixed positioning */}
            {menuOpen && (
              <motion.div
                ref={menuRef} // Add ref to the menu
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  width: isDesktop ? '250px' : '200px',
                  backgroundColor: 'white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  borderRadius: '0 0 8px 8px',
                  overflow: 'hidden',
                  zIndex: 1000,
                }}
              >
                {/* Menu items remain the same as before */}
                {Object.keys(sections).map((option) => (
                  <motion.div
                    key={option}
                    whileHover={{ backgroundColor: '#f5f5f5' }}
                    style={{
                      padding: '1rem 1.5rem',
                      borderBottom: '1px solid #f0f0f0',
                      cursor: 'pointer',
                      backgroundColor: selectedOption === option ? '#e6f0ff' : 'white',
                      color: selectedOption === option ? '#0066cc' : '#333',
                      fontWeight: selectedOption === option ? '600' : '500',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={() => {
                      setSelectedOption(option);
                      setMenuOpen(false);
                      if (articleSlug) {
                        navigate('/info-center');
                      }
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    {option}
                  </motion.div>
                ))}



                <motion.div
                  whileHover={{ backgroundColor: '#f5f5f5' }}
                  style={{
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onClick={() => {
                    navigate('/about');
                    setMenuOpen(false);
                  }}
                >
                  About
                </motion.div>

                <motion.div
                  whileHover={{ backgroundColor: '#f5f5f5' }}
                  style={{
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onClick={() => {
                    setShowPaymentPopup(true);
                    setMenuOpen(false);
                  }}
                >
                  Subscribe
                </motion.div>

                <motion.div
                  whileHover={{ backgroundColor: '#f5f5f5' }}
                  style={{
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onClick={() => {
                    navigate('/advertise');
                    setMenuOpen(false);
                  }}
                >
                  Advertise
                </motion.div>

                <motion.div
                  whileHover={{ backgroundColor: '#f5f5f5' }}
                  style={{
                    padding: '1rem 1.5rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                  }}
                  onClick={() => {
                    setShowAuthPopup(true);
                    setMenuOpen(false);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#0066cc"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  Login / Sign Up
                </motion.div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    );
  }



  function Section({ image, title, quote, content, tagline, date, slug, isBlogPost }) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        onClick={() => isBlogPost && handleArticleClick(slug)}
        style={{
          cursor: isBlogPost ? 'pointer' : 'default',
          width: '100%',
          maxWidth: isDesktop ? '840px' : '100%',
          margin: isDesktop ? '0 auto 2rem' : '0 0 1.5rem 0',
          backgroundColor: isDesktop ? '#fff' : 'transparent',
          borderRadius: isDesktop ? '15px' : '0',
          boxShadow: isDesktop ? '0 4px 24px rgba(0, 0, 0, 0.1)' : 'none',
          padding: isDesktop ? '1.25rem' : '0'
        }}
      >
        <div style={{ width: '100%' }}>
          <img
            src={image}
            alt={title}
            referrerPolicy="no-referrer"
            style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: isDesktop ? '12px' : '0',
              marginBottom: isDesktop ? '1.25rem' : '0.75rem'
            }}
          />
        </div>

        <div
          style={{
            width: '100%',
            maxWidth: isDesktop ? '760px' : '100%',
            margin: '0 auto',
            padding: isDesktop ? '0 1rem' : '0'
          }}
        >
          <h2
            style={{
              fontSize: isDesktop ? '1.6rem' : '1.25rem',
              fontWeight: 'bold',
              marginBottom: '0.75rem',
              textAlign: 'left',
              lineHeight: '1.25',
              color: '#1a1a1a',
              letterSpacing: '-0.02em'
            }}
          >
            {title}
          </h2>

          {tagline && date && (
            <div
              style={{
                textAlign: 'left',
                color: '#666',
                marginBottom: '0.75rem',
                fontSize: isDesktop ? '0.9rem' : '0.85rem'
              }}
            >
              <span style={{ fontWeight: '600', color: '#0066cc' }}>
                {tagline}
              </span>
              {' '}• {date}
            </div>
          )}

          <blockquote
            style={{
              fontStyle: 'italic',
              marginBottom: '1rem',
              color: '#444',
              textAlign: 'left',
              fontSize: isDesktop ? '1.05rem' : '1rem',
              lineHeight: '1.6',
              padding: isDesktop ? '1rem' : '0',
              borderLeft: isDesktop ? '2px solid #0066cc' : 'none',
              backgroundColor: isDesktop ? '#f8f9fa' : 'transparent'
            }}
          >
            {quote}
          </blockquote>

          {content && (
            <p
              style={{
                fontSize: isDesktop ? '1.02rem' : '1rem',
                lineHeight: '1.7',
                color: '#374151',
                marginBottom: '1rem'
              }}
            >
              {content}
            </p>
          )}
        </div>
      </motion.div>
    );
  }

  function RelatedArticlesSection() {
    const relatedArticles = getRelatedArticles();
    if (relatedArticles.length === 0) return null;
    const sectionName = isInBlogPosts ? 'News & Blogs' : 'Top Loans';

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          width: '100%',
          maxWidth: isDesktop ? '840px' : '100%',
          margin: isDesktop ? '1.5rem auto 2rem' : '1.5rem 0 2rem',
          backgroundColor: isDesktop ? '#fff' : 'transparent',
          borderRadius: isDesktop ? '15px' : '0',
          boxShadow: isDesktop ? '0 4px 24px rgba(0, 0, 0, 0.1)' : 'none',
          padding: isDesktop ? '1.25rem' : '1rem',
          boxSizing: 'border-box',
        }}
      >
        <h2
          style={{
            fontSize: isDesktop ? '1.4rem' : '1.2rem',
            fontWeight: 'bold',
            marginBottom: '1.5rem',
            color: '#1a1a1a',
            letterSpacing: '-0.02em',
            textAlign: 'center'
          }}
        >
          Read More Articles in {sectionName}
        </h2>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}
        >
          <div
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              color: '#666'
            }}
          >
            <span style={{ fontWeight: '600' }}>Today:</span> {currentDate}
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isDesktop ? 'repeat(auto-fill, minmax(300px, 1fr))' : '1fr',
            gap: '1.25rem',
            marginTop: '1.25rem'
          }}
        >
          {relatedArticles.slice(0, 3).map((article) => (
            <motion.div
              key={article.slug}
              whileHover={{ scale: 1.03 }}
              style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer'
              }}
              onClick={() => handleArticleClick(article.slug)}
            >
              <img
                src={article.headerImage}
                alt={article.title}
                referrerPolicy="no-referrer"
                style={{
                  width: '100%',
                  height: '160px',
                  objectFit: 'cover'
                }}
              />
              <div style={{ padding: '1.5rem' }}>
                <h3
                  style={{
                    fontSize: '1.15rem',
                    fontWeight: 'bold',
                    marginBottom: '0.5rem',
                    color: '#1a1a1a',
                  }}
                >
                  {article.title}
                </h3>
                <p
                  style={{
                    fontSize: '0.9rem',
                    color: '#666',
                    marginBottom: '1rem'
                  }}
                >
                  {article.date}
                </p>
                <p
                  style={{
                    fontSize: '1rem',
                    color: '#374151',
                    marginBottom: '1rem'
                  }}
                >
                  {article.mainQuote}
                </p>
                <div
                  style={{
                    display: 'inline-block',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#0066cc',
                    color: 'white',
                    borderRadius: '6px',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}
                >
                  Read Article
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    );
  }

  function ReadNextSection({ prevDate }) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          width: '100%',
          maxWidth: isDesktop ? '840px' : '100%',
          margin: isDesktop ? '1.5rem auto 2rem' : '1.5rem 0 2rem',
          backgroundColor: isDesktop ? '#fff' : 'transparent',
          borderRadius: isDesktop ? '15px' : '0',
          boxShadow: isDesktop ? '0 4px 24px rgba(0, 0, 0, 0.1)' : 'none',
          padding: isDesktop ? '1.25rem' : '1rem',
          textAlign: 'center'
        }}
      >
        <h2
          style={{
            fontSize: isDesktop ? '1.4rem' : '1.2rem',
            fontWeight: 'bold',
            marginBottom: '1rem',
            color: '#1a1a1a',
            letterSpacing: '-0.02em'
          }}
        >
          Read Next
        </h2>
        <h3
          style={{
            fontSize: isDesktop ? '1.15rem' : '1.05rem',
            fontWeight: '600',
            marginBottom: '1.5rem',
            color: '#333',
          }}
        >
          Other Recent Articles in Top Loans
        </h3>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '2rem',
            flexWrap: 'wrap',
            marginTop: '1rem'
          }}
        >
          <div
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              color: '#666'
            }}
          >
            <span style={{ fontWeight: '600' }}>Today:</span> {currentDate}
          </div>
          <div
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              color: '#666'
            }}
          >
            <span style={{ fontWeight: '600' }}>Previous Article:</span> {prevDate}
          </div>
        </div>

        <div style={{ marginTop: '2rem', animation: 'bounce 2s infinite' }}>
          <ChevronDown size={32} color="#0066cc" />
        </div>
      </motion.div>
    );
  }

  // 3) Combine all bottom buttons (Contact, Login/Signup, Subscribe)
  function ActionButtonsSection() {
    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '1rem',
          margin: isDesktop ? '3rem auto 2rem' : '2rem auto',
          maxWidth: isDesktop ? '800px' : '100%',
          alignItems: 'center'
        }}
      >
        {/* Contact (mailto) */}
        <motion.a
          href="mailto:contact@satoshiadvance.com"
          whileHover={{ scale: 1.05, backgroundColor: '#005bb7' }}
          whileTap={{ scale: 0.95 }}
          style={{
            display: 'inline-block',
            backgroundColor: '#0066cc',
            color: 'white',
            padding: isDesktop ? '1rem 1.5rem' : '0.75rem 1.25rem',
            borderRadius: '12px',
            fontSize: isDesktop ? '1rem' : '1.2rem',
            fontWeight: '600',
            textDecoration: 'none',
            boxShadow: '0 4px 12px rgba(0, 102, 204, 0.2)'
          }}
        >
          Contact
        </motion.a>

        {/* Login/Signup (show Auth popup) */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{
            backgroundColor: '#0066cc',
            color: 'white',
            padding: isDesktop ? '1rem 1.5rem' : '0.75rem 1.25rem',
            borderRadius: '12px',
            fontSize: isDesktop ? '1rem' : '1.2rem',
            fontWeight: '600',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 102, 204, 0.2)'
          }}
          onClick={() => setShowAuthPopup(true)}
        >
          Login/Signup
        </motion.button>



        {/* Subscribe (show Payment popup) */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{
            backgroundColor: '#0066cc',
            color: 'white',
            padding: isDesktop ? '1rem 1.5rem' : '0.75rem 1.25rem',
            borderRadius: '12px',
            fontSize: isDesktop ? '1rem' : '1.2rem',
            fontWeight: '600',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 102, 204, 0.2)'
          }}
          onClick={() => setShowPaymentPopup(true)}
        >
          Subscribe
        </motion.button>
      </div>
    );
  }

  // Ensure a valid content array for the blog post
  function getFullBlogPost(post) {
    if (!post) return post;
    return {
      ...post,
      content: Array.isArray(post.content) && post.content.length > 0
        ? post.content
        : [
          {
            type: 'regular',
            text: `This article covers key aspects of ${post.title} including their services, history, and features.`
          },
          ...(post.keyTakeaways && post.keyTakeaways.length > 0
            ? [{
              type: 'bullet-points',
              heading: 'Key Highlights',
              points: post.keyTakeaways
            }]
            : [])
        ]
    };
  }

  // Compact browsable list for Top Loans (full article opens on tap)
  const renderTopLoansView = () => {
    if (selectedOption === 'Top Loans' && !articleSlug) {
      const orderedPosts = getOrderedLoanPosts();
      if (orderedPosts.length === 0) return null;
      const items = orderedPosts.map((post) => ({
        title: post.title,
        date: post.date,
        tagline: post.category,
        quote: post.mainQuote,
        slug: post.slug,
        image: post.headerImage
      }));

      return (
        <>
          {browseControls(items)}
          <ArticleListCard
            items={browseFilter(items)}
            isDesktop={isDesktop}
            onOpen={handleArticleClick}
          />
        </>
      );
    }
    return null;
  };

  // Bouncing arrow CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes bounce {
        0%, 20%, 50%, 80%, 100% {
          transform: translateY(0);
        }
        40% {
          transform: translateY(-20px);
        }
        60% {
          transform: translateY(-10px);
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Per-article SEO (title, description, canonical, Open Graph, Twitter, JSON-LD).
  const seo = currentArticle ? getArticleSeo(currentArticle, articleSlug) : null;

  return (
    <div
      id="top"
      style={{
        backgroundColor: isDesktop ? '#f5f5f5' : '#fff',
        minHeight: '100vh',
        padding: isDesktop ? '1.25rem' : '0.75rem',
        overflowX: 'hidden'
      }}
    >
      {/* SEO metadata */}
      {seo ? (
        <Helmet>
          <title>{seo.pageTitle}</title>
          <meta name="description" content={seo.description} />
          <link rel="canonical" href={seo.url} />
          <meta property="og:type" content="article" />
          <meta property="og:site_name" content="Satoshi Advance News" />
          <meta property="og:title" content={seo.pageTitle} />
          <meta property="og:description" content={seo.description} />
          <meta property="og:url" content={seo.url} />
          {seo.image && <meta property="og:image" content={seo.image} />}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={seo.pageTitle} />
          <meta name="twitter:description" content={seo.description} />
          {seo.image && <meta name="twitter:image" content={seo.image} />}
          <script type="application/ld+json">{JSON.stringify(seo.jsonLd)}</script>
        </Helmet>
      ) : (
        <Helmet>
          <title>Satoshi Advance News | Crypto Lending, DeFi &amp; Bitcoin</title>
          <meta
            name="description"
            content="The latest news and blogs on crypto lending, DeFi, Bitcoin, and digital finance from Satoshi Advance."
          />
          <link rel="canonical" href={`${SITE_BASE_URL}/info-center`} />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Satoshi Advance News" />
          <meta property="og:title" content="Satoshi Advance News" />
          <meta
            property="og:description"
            content="The latest news and blogs on crypto lending, DeFi, Bitcoin, and digital finance."
          />
          <meta property="og:url" content={`${SITE_BASE_URL}/info-center`} />
        </Helmet>
      )}

      {/* Header */}
      <HeaderBar />
      {/* Spacer so content isn't hidden under the fixed header */}
      <div style={{ height: isDesktop ? '80px' : '60px' }}></div>

      {/* If an article is selected */}
      {articleSlug && currentArticle && (
        <>
          <BlogPost post={getFullBlogPost(currentArticle)} isDesktop={isDesktop} />
          <PrevNextNav />
          <RelatedArticlesSection />
        </>
      )}


      {/* If on Top Loans tab (and no article selected), show the full blog posts */}
      {!articleSlug && renderTopLoansView()}

      {/* If Resources tab */}
      {selectedOption === 'Resources' && !articleSlug && (
        <ResourceSection resources={ResourceData} isDesktop={isDesktop} />
      )}

      {/*
  Render PopularLinksTab if "Popular Links" is selected
  and no article is open (no articleSlug)
*/}
      {selectedOption === "Popular Links" && !articleSlug && (
        <PopularLinksTab isDesktop={isDesktop} />
      )}

      {/*
  Otherwise, show the default "cards"
  (News & Blogs, Top Loans, etc.)
*/}
      {selectedOption !== "Resources" &&
        selectedOption !== "Popular Links" && // <-- key line
        !(selectedOption === "Top Loans" && !articleSlug) &&
        !articleSlug &&
        (filteredContent().length > 0 && filteredContent().every((item) => item.isBlogPost)
          ? (
            <>
              {browseControls(filteredContent())}
              <ArticleListCard
                items={browseFilter(filteredContent())}
                isDesktop={isDesktop}
                onOpen={handleArticleClick}
              />
            </>
          )
          : filteredContent().map((item, index) => (
            <Section
              key={item.slug || index}
              isDesktop={isDesktop}
              {...item}
            />
          )))
      }


      {/* Bottom Action Buttons */}
      <ActionButtonsSection />

      {/* Back to Top link */}
      <div style={{ textAlign: 'center', marginTop: '2rem', marginBottom: '2rem' }}>
        <a
          href="#top"
          style={{
            textDecoration: 'none',
            color: '#0066cc',
            fontWeight: 'bold',
            fontSize: isDesktop ? '1.2rem' : '1rem'
          }}
        >
          Back to Top
        </a>
      </div>

      {/* Floating back-to-top for long pages: chunky pixel arrow, retro
          square button with a hard offset shadow */}
      {showScrollTop && (
        <button
          type="button"
          aria-label="Back to top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            bottom: '22px',
            right: '18px',
            width: '46px',
            height: '46px',
            borderRadius: '8px',
            border: '2px solid #ffffff',
            backgroundColor: '#0066cc',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '3px 3px 0 rgba(0, 0, 0, 0.25)',
            zIndex: 900
          }}
        >
          <svg width="22" height="22" viewBox="0 0 7 7" shapeRendering="crispEdges" aria-hidden="true">
            <g fill="#ffffff">
              <rect x="3" y="0" width="1" height="1" />
              <rect x="2" y="1" width="3" height="1" />
              <rect x="1" y="2" width="5" height="1" />
              <rect x="0" y="3" width="7" height="1" />
              <rect x="2" y="4" width="3" height="3" />
            </g>
          </svg>
        </button>
      )}

      {/* 4) Conditionally render the popups */}
      {showAuthPopup && (
        <AuthPopup
          onClose={() => setShowAuthPopup(false)}
          fromInfoCenter={true}
          openPaymentPopup={(paymentAmount) => {
            setAmount(paymentAmount); // Set the amount from the selected plan
            setShowAuthPopup(false);
            setTimeout(() => {
              setShowPaymentPopup(true);
            }, 300);
          }}
        />
      )}
      {showPaymentPopup && (
        <PaymentPopup
          onClose={() => setShowPaymentPopup(false)}
          amount={amount} // Pass the selected amount
        />
      )}
    </div>
  );
}

export default InfoCenter;


